/**
 * Utility functions for working with license types.
 */

import type { LicenseTypeResponseSchema } from "@/hera-api/client";

/** Sort key: operator-configured `display_order`, then name, then id. */
export function compareLicenseTypesByDisplayOrder(
  a: Pick<LicenseTypeResponseSchema, "name" | "id"> & {
    display_order?: number | null;
  },
  b: Pick<LicenseTypeResponseSchema, "name" | "id"> & {
    display_order?: number | null;
  },
): number {
  const ao = a.display_order ?? 0;
  const bo = b.display_order ?? 0;
  if (ao !== bo) return ao - bo;
  const nc = a.name.localeCompare(b.name);
  if (nc !== 0) return nc;
  return a.id.localeCompare(b.id);
}

export function sortLicenseTypesByDisplayOrder<
  T extends Pick<LicenseTypeResponseSchema, "name" | "id"> & {
    display_order?: number | null;
  },
>(licenseTypes: T[]): T[] {
  return [...licenseTypes].sort(compareLicenseTypesByDisplayOrder);
}

/**
 * Filters out license types that are marked as "under_development" or deleted.
 * This is used in the registrant portal to only show license types that are
 * available for registration.
 *
 * @param licenseTypes - Array of license types from the API
 * @returns Filtered array of license types available for registration
 */
export function filterAvailableLicenseTypes(
  licenseTypes: LicenseTypeResponseSchema[] | undefined,
): LicenseTypeResponseSchema[] {
  if (!licenseTypes) return [];

  const filtered = licenseTypes.filter(
    (licenseType) =>
      // Exclude deleted license types
      !licenseType.deleted_at &&
      // Exclude license types under development
      !licenseType.under_development,
  );
  return sortLicenseTypesByDisplayOrder(filtered);
}

/**
 * Checks if a specific license type is available for registration.
 *
 * @param licenseType - The license type to check
 * @returns true if the license type is available for registration
 */
export function isLicenseTypeAvailable(
  licenseType: LicenseTypeResponseSchema | undefined,
): boolean {
  if (!licenseType) return false;
  return !licenseType.deleted_at && !licenseType.under_development;
}

/**
 * Filters out only deleted license types, keeping those marked as "under_development".
 * This is used in operator contexts where under-development license types should be
 * selectable (e.g. jurisdiction import, dashboard filtering).
 *
 * @param licenseTypes - Array of license types from the API
 * @returns Filtered array including active and under-development license types
 */
export function filterOperatorLicenseTypes(
  licenseTypes: LicenseTypeResponseSchema[] | undefined,
): LicenseTypeResponseSchema[] {
  if (!licenseTypes) return [];
  const filtered = licenseTypes.filter(
    (licenseType) => !licenseType.deleted_at,
  );
  return sortLicenseTypesByDisplayOrder(filtered);
}
